import { useEffect, useState } from "react";
import axios from "axios";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { AfricaMap } from "./components/AfricaMap";
import { useReveal } from "./hooks/useReveal";
import heroImg from "./assets/hero-africa.jpg";
import founderImg from "./assets/founder.jpg";
import managerMsgImg from "./assets/manager-message.jpg";
import solSante from "./assets/sol-sante.jpg";
import solMoney from "./assets/sol-money.jpg";
import solEdu from "./assets/sol-edu.jpg";
import solSejour from "./assets/sol-sejour.jpg";
import solDigital from "./assets/sol-digital.jpg";
import {
  HeartPulse, Wallet, GraduationCap, Building2, Plane,
  ShoppingBag, Cpu, Briefcase, ArrowRight, CheckCircle2,
  Globe2, Sparkles, Handshake, ShieldCheck, Rocket, Users,
  Facebook, Linkedin, Instagram, MessageCircle, Mail, Phone, MapPin,
  Building, FlaskConical, School, Landmark, Hotel,
  Download, Quote, ChevronDown, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESENTATION_IMG = "/findeo-presentation.png";
const CONTACT_EMAIL_ENDPOINT = "https://formsubmit.co/ajax/info@groupefindeo.com";

const solutions = [
  {
    title: "Obled Santé", tagline: "Santé connectée", image: solSante,
    color: "from-royal/90 to-primary/80",
    desc: "Écosystème numérique visant à rapprocher la diaspora africaine des besoins de santé de leurs proches restés sur le continent à travers des solutions d'accompagnement innovantes et sécurisées.",
    features: ["Accompagnement médical à distance", "Facilitation de prise en charge pour les familles", "Réseau d'infrastructures partenaires", "Suivi et continuité des soins", "Solutions dédiées à la diaspora"],
  },
  {
    title: "Obled Money", tagline: "Fintech", image: solMoney,
    color: "from-emerald/90 to-royal/80",
    desc: "Solution financière technologique simplifiant les flux et les interactions économiques entre la diaspora africaine et le continent, tout en maximisant l'impact des transferts sur le quotidien des familles.",
    features: ["Passerelle financière internationale", "Services de transfert simplifiés", "Outils de gestion pour les familles", "Inclusion financière et digitale", "Transactions sécurisées 24h/24"],
  },
  {
    title: "Renfo500", tagline: "Éducation", image: solEdu,
    color: "from-royal/90 to-primary",
    desc: "Plateforme technologique éducative d'excellence conçue pour accompagner la réussite des apprenants et valoriser les compétences des enseignants grâce à des formats pédagogiques modernes.",
    features: ["Renforcement scolaire de proximité", "Accompagnement pédagogique personnalisé", "Environnement d'apprentissage numérique", "Suivi de la progression des apprenants", "Valorisation du corps enseignant"],
  },
  {
    title: "Séjour Afrique francophone", tagline: "Hébergement", image: solSejour,
    color: "from-emerald/80 to-royal/80",
    desc: "Plateforme technologique de mise en relation et de valorisation des infrastructures d'hébergement touristique et professionnel à travers l'Afrique francophone.",
    features: ["Réservation et mise en relation simplifiées", "Valorisation des patrimoines immobiliers", "Outils de gestion pour les hôtes", "Standardisation de la qualité d'accueil", "Promotion du tourisme local et professionnel"],
  },
  {
    title: "Solutions Digitales FINDEO", tagline: "Transformation numérique", image: solDigital,
    color: "from-primary to-royal",
    desc: "Cabinet d'ingénierie et de conseil technologique accompagnant les entreprises, institutions et ONG africaines dans la réussite de leur transition numérique et l'optimisation de leurs processus.",
    features: ["Conseil stratégique en technologies", "Ingénierie logicielle et plateformes métiers", "Modernisation et digitalisation des processus", "Infrastructure et sécurité des données", "Maintenance et support opérationnel"],
  },
];

const sectors = [
  { icon: HeartPulse, label: "Santé" },
  { icon: Wallet, label: "Fintech" },
  { icon: GraduationCap, label: "Éducation" },
  { icon: Building2, label: "Immobilier" },
  { icon: Plane, label: "Tourisme" },
  { icon: ShoppingBag, label: "E-commerce" },
  { icon: Cpu, label: "Transformation numérique" },
  { icon: Briefcase, label: "Services aux entreprises" },
];

const reasons = [
  { icon: Globe2, title: "Expertise internationale", desc: "Représentant officiel de Obled Corporation en Afrique." },
  { icon: Rocket, title: "Innovation", desc: "Solutions conçues avec les technologies les plus modernes." },
  { icon: Users, title: "Réseau africain", desc: "Présence progressive dans plusieurs pays francophones." },
  { icon: ShieldCheck, title: "Sécurité", desc: "Protection des données et des transactions." },
  { icon: Handshake, title: "Accompagnement", desc: "Support dédié aux utilisateurs et partenaires." },
  { icon: Sparkles, title: "Impact social", desc: "Des solutions créées pour améliorer la vie des populations." },
];

const partnerCats = [
  { icon: Building, title: "Hôpitaux & Cliniques", platform: "Obled Santé" },
  { icon: FlaskConical, title: "Pharmacies & Laboratoires", platform: "Obled Santé" },
  { icon: School, title: "Écoles & Universités", platform: "Renfo500" },
  { icon: Landmark, title: "Banques & Fintech", platform: "Obled Money" },
  { icon: Hotel, title: "Hôtels & Résidences", platform: "Séjour Afrique francophone" },
  { icon: Briefcase, title: "Entreprises & Administrations", platform: "Solutions Digitales FINDEO" },
];

const impacts = [
  { k: "10 000+", v: "Familles accompagnées", icon: Users },
  { k: "500+", v: "Établissements partenaires visés", icon: Building },
  { k: "100+", v: "Professionnels partenaires", icon: Briefcase },
  { k: "10+", v: "Pays ciblés", icon: Globe2 },
  { k: "24/7", v: "Disponibilité des services", icon: ShieldCheck },
];

const ecosystem = [
  { label: "Diaspora", desc: "Le point de départ de l'écosystème.", icon: Users },
  { label: "Obled Money", desc: "Transferts & services financiers.", icon: Wallet },
  { label: "Obled Santé", desc: "Prise en charge médicale à distance.", icon: HeartPulse },
  { label: "Renfo500", desc: "Éducation & formation.", icon: GraduationCap },
  { label: "Séjour Afrique francophone", desc: "Hébergement & tourisme.", icon: Plane },
  { label: "Solutions Digitales FINDEO", desc: "Transformation numérique.", icon: Cpu },
];

const visionPillars = ["Santé numérique", "Inclusion financière", "Éducation numérique", "Hébergement touristique", "Transformation digitale"];

export default function App() {
  useReveal();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <Hero />
      <About />
      <Solutions />
      <Ecosystem />
      <Impact />
      <Sectors />
      <Vision />
      <Partnerships />
      <Presence />
      <Why />
      <President />
      <Investors />
      <Contact />
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section id="accueil" className="relative min-h-[100svh] flex items-center overflow-hidden">
      <img src={heroImg} alt="Afrique numérique" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-hero opacity-90" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, oklch(0.7 0.19 50 / 0.4), transparent 60%)" }} />
      <div className="relative container mx-auto max-w-7xl px-4 sm:px-6 pt-32 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-9 text-white">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-medium">
            <span className="h-2 w-2 rounded-full bg-royal animate-pulseDot" />
            Représentant officiel de Obled Corporation en Afrique
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
            GROUPE <span className="text-gradient-brand">FINDEO</span>
            <span className="block text-white/90 text-2xl sm:text-4xl lg:text-5xl font-semibold mt-3">
              Construisons ensemble l'Afrique numérique de demain
            </span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
            Le Groupe Findeo développe des solutions innovantes dans les domaines de la santé,
            de la finance, de l'éducation, du tourisme et des services numériques afin
            d'accompagner la transformation de l'Afrique francophone.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#solutions" data-testid="hero-solutions-cta" className="inline-flex items-center gap-2 rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-semibold text-white shadow-elegant hover:scale-[1.03] transition">
              Découvrir nos solutions <ArrowRight className="h-4 w-4" />
            </a>
            <a href="#contact" data-testid="hero-contact-cta" className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/30 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/20 transition">
              Nous contacter
            </a>
          </div>
          <div className="mt-12 grid grid-cols-3 max-w-xl gap-6 text-white">
            {[{ k: "9+", v: "Pays ciblés" }, { k: "5", v: "Plateformes" }, { k: "2021", v: "Année de création" }].map((s) => (
              <div key={s.v}>
                <div className="text-3xl sm:text-4xl font-bold text-gradient-brand font-display">{s.k}</div>
                <div className="text-xs sm:text-sm text-white/70 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs tracking-widest animate-bounce">DÉFILER</div>
    </section>
  );
}

function SectionTag({ children }) {
  return <span className="text-xs font-semibold tracking-widest text-royal uppercase">{children}</span>;
}

function About() {
  return (
    <section id="apropos" className="py-24 sm:py-32">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
        <div className="reveal relative">
          <div className="absolute -inset-4 bg-gradient-brand rounded-3xl opacity-20 blur-2xl" />
          <img src={founderImg} alt="Connecter la diaspora africaine à l'Afrique par la technologie" className="relative rounded-3xl shadow-elegant w-full object-cover aspect-[4/5]" loading="lazy" />
          <div className="absolute -bottom-6 -right-4 sm:right-6 bg-background rounded-2xl shadow-elegant border border-border px-6 py-4">
            <div className="text-sm text-muted-foreground">Diaspora &amp; Afrique</div>
            <div className="font-display font-semibold text-lg">Connectées par la tech</div>
          </div>
        </div>
        <div className="reveal">
          <SectionTag>Qui sommes-nous</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">
            La technologie au service du lien entre la diaspora et l'Afrique
          </h2>
          <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
            <p>Le <strong className="text-foreground">Groupe FINDEO</strong> bâtit des ponts numériques entre la diaspora africaine et le continent. Nos plateformes permettent à des millions de familles d'agir à distance, en toute confiance, pour leurs proches restés en Afrique.</p>
            <p>Représentant africain d'<strong className="text-foreground">Obled Corporation</strong> (États-Unis), FINDEO conçoit des solutions dans la santé, le paiement, l'éducation et la logistique pour connecter les diasporas de Paris, New York, Montréal ou Bruxelles aux réalités africaines.</p>
            <p>Notre ambition : faire de la technologie le trait d'union le plus fiable entre l'Afrique et ses enfants partout dans le monde.</p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {[{ t: "Vision", d: "Une diaspora connectée à l'Afrique par un écosystème numérique unifié." }, { t: "Mission", d: "Permettre à chaque Africain, ici ou ailleurs, d'agir pour les siens en un clic." }].map((b) => (
              <div key={b.t} className="rounded-2xl border border-border p-5 bg-secondary/50">
                <div className="text-sm font-display font-semibold text-royal">{b.t}</div>
                <p className="mt-1 text-sm text-muted-foreground">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Solutions() {
  return (
    <section id="solutions" className="py-24 sm:py-32 bg-secondary/40">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl reveal">
          <SectionTag>Nos solutions</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">Cinq plateformes pour transformer l'Afrique</h2>
          <p className="mt-4 text-muted-foreground text-lg">Des produits pensés pour les populations africaines et leur diaspora, développés et opérés depuis Abidjan.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((s, i) => (
            <article key={s.title} data-testid={`solution-card-${i}`} className={`reveal group relative overflow-hidden rounded-3xl bg-card border border-border shadow-soft hover:shadow-elegant hover:-translate-y-1 transition-all duration-300 ${i === 4 ? "md:col-span-2 lg:col-span-1" : ""}`}>
              <div className="relative h-48 overflow-hidden">
                <img src={s.image} alt={s.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                <div className={`absolute inset-0 bg-gradient-to-br ${s.color} mix-blend-multiply opacity-80`} />
                <span className="absolute top-4 left-4 text-[10px] tracking-widest font-semibold text-white/90 uppercase bg-black/25 backdrop-blur px-2.5 py-1 rounded-full">{s.tagline}</span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-primary">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                <ul className="mt-4 space-y-1.5">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground/80">
                      <CheckCircle2 className="h-4 w-4 text-royal shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Ecosystem() {
  return (
    <section id="ecosysteme" className="py-24 sm:py-32">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl reveal">
          <SectionTag>Notre écosystème</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">Un écosystème numérique au service de l'Afrique</h2>
          <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
            Le Groupe FINDEO développe un ensemble de plateformes complémentaires destinées à répondre aux besoins quotidiens des populations africaines et de leur diaspora.
            Grâce à notre partenariat stratégique avec <strong className="text-foreground">Obled Corporation (USA)</strong>, nous construisons un écosystème connectant santé, finance, éducation, immobilier et services numériques.
          </p>
        </div>
        <div className="mt-16 relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-royal/30 to-transparent hidden md:block" />
          <div className="grid gap-6 md:gap-10">
            {ecosystem.map(({ label, desc, icon: Icon }, i) => (
              <div key={label} className={`reveal flex flex-col md:flex-row items-center gap-5 ${i % 2 ? "md:flex-row-reverse" : ""}`}>
                <div className="md:w-1/2 flex md:justify-end">
                  <div className={`relative w-full max-w-sm rounded-2xl bg-card border border-border p-6 shadow-soft hover:shadow-elegant transition ${i % 2 ? "md:text-left" : "md:text-right"}`}>
                    <div className={`flex items-center gap-3 ${i % 2 ? "" : "md:flex-row-reverse"}`}>
                      <div className="h-12 w-12 rounded-xl bg-gradient-brand text-white flex items-center justify-center shrink-0"><Icon className="h-6 w-6" /></div>
                      <div className="font-display font-bold text-lg text-primary">{label}</div>
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground">{desc}</p>
                  </div>
                </div>
                <div className="hidden md:flex h-10 w-10 rounded-full bg-gradient-brand text-white items-center justify-center text-xs font-bold shadow-elegant z-10">{i + 1}</div>
                <div className="md:w-1/2" />
                {i < ecosystem.length - 1 && <ChevronDown className="md:hidden h-6 w-6 text-royal/40" />}
              </div>
            ))}
          </div>
        </div>
        <p className="mt-16 max-w-2xl mx-auto text-center text-sm text-muted-foreground reveal">
          <span className="font-semibold text-foreground">Afrique • Innovation • Inclusion • Transformation.</span>
        </p>
      </div>
    </section>
  );
}

function Impact() {
  return (
    <section className="py-24 sm:py-32 bg-secondary/40">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl reveal">
          <SectionTag>Notre impact</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">Notre impact sur les populations</h2>
        </div>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {impacts.map(({ k, v, icon: Icon }) => (
            <div key={v} className="reveal rounded-3xl border border-border bg-card p-6 text-center hover:shadow-elegant hover:-translate-y-1 transition">
              <div className="h-12 w-12 mx-auto rounded-2xl bg-gradient-brand text-white flex items-center justify-center mb-4"><Icon className="h-6 w-6" /></div>
              <div className="text-3xl sm:text-4xl font-bold text-gradient-brand font-display">{k}</div>
              <div className="mt-2 text-xs sm:text-sm text-muted-foreground leading-snug">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Sectors() {
  return (
    <section id="secteurs" className="py-24 sm:py-32">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl reveal">
          <SectionTag>Secteurs d'intervention</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">Des expertises au service de l'Afrique</h2>
        </div>
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {sectors.map(({ icon: Icon, label }) => (
            <div key={label} className="reveal group flex flex-col items-start gap-3 rounded-2xl border border-border p-6 hover:border-royal hover:-translate-y-1 transition-all bg-card">
              <div className="h-12 w-12 rounded-xl bg-gradient-brand flex items-center justify-center text-white shadow-soft"><Icon className="h-6 w-6" /></div>
              <div className="font-semibold text-foreground">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Vision() {
  return (
    <section id="vision" className="py-24 sm:py-32 bg-primary text-primary-foreground relative overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at top left, oklch(0.7 0.19 50 / 0.35), transparent 60%)" }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at bottom right, oklch(0.55 0.18 42 / 0.4), transparent 60%)" }} />
      <div className="relative container mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
        <div className="reveal">
          <SectionTag>Vision 2030</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">Devenir un acteur technologique panafricain de référence</h2>
          <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-xl">Notre ambition est de devenir l'un des groupes technologiques africains les plus influents et de connecter plusieurs millions d'utilisateurs à travers l'Afrique francophone et la diaspora.</p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {visionPillars.map((p) => (
              <li key={p} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-5 w-5 text-royal shrink-0" /> {p}</li>
            ))}
          </ul>
        </div>
        <div className="reveal relative">
          <div className="aspect-square rounded-full bg-gradient-brand opacity-30 blur-3xl absolute inset-0" />
          <div className="relative aspect-square rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-xl p-10 flex flex-col items-center justify-center text-center">
            <Globe2 className="h-20 w-20 text-royal" strokeWidth={1.2} />
            <div className="mt-6 text-6xl font-bold text-gradient-brand font-display">2030</div>
            <div className="mt-3 text-white/80 max-w-xs">Une Afrique connectée, inclusive et prospère grâce au numérique.</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Partnerships() {
  return (
    <section id="partenaires" className="py-24 sm:py-32 bg-secondary/40">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl reveal">
          <SectionTag>Opportunités de partenariat</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">Rejoignez notre réseau de partenaires</h2>
          <p className="mt-4 text-muted-foreground text-lg">Nous collaborons avec des institutions de santé, d'éducation, financières, touristiques et des entreprises à travers l'Afrique francophone.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {partnerCats.map(({ icon: Icon, title, platform }) => (
            <div key={title} className="reveal group rounded-3xl border border-border bg-card p-7 hover:border-royal hover:-translate-y-1 hover:shadow-elegant transition">
              <div className="h-14 w-14 rounded-2xl bg-royal/10 text-royal flex items-center justify-center group-hover:bg-gradient-brand group-hover:text-white transition"><Icon className="h-7 w-7" /></div>
              <div className="mt-5 font-display font-bold text-lg text-primary">{title}</div>
              <div className="mt-1 text-xs font-semibold tracking-widest uppercase text-royal">Pour {platform}</div>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center reveal">
          <a href="#contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-brand px-7 py-3.5 text-sm font-semibold text-white shadow-elegant hover:scale-[1.03] transition">
            Devenir partenaire <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function Presence() {
  return (
    <section id="presence" className="py-24 sm:py-32 bg-primary text-primary-foreground relative overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at left, oklch(0.55 0.18 42 / 0.45), transparent 60%)" }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at bottom right, oklch(0.7 0.19 50 / 0.4), transparent 60%)" }} />
      <div className="relative container mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div className="reveal">
          <SectionTag>Présence en Afrique</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">Une ambition continentale</h2>
          <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-xl">Notre objectif est de déployer progressivement nos solutions dans toute l'Afrique francophone afin de connecter les populations aux services essentiels.</p>
        </div>
        <div className="reveal"><AfricaMap /></div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section className="py-24 sm:py-32">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl reveal">
          <SectionTag>Pourquoi nous choisir</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">Six raisons de bâtir avec Findeo</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="reveal relative rounded-3xl p-7 border border-border bg-card hover:shadow-elegant transition group">
              <div className="h-14 w-14 rounded-2xl bg-royal/10 text-royal flex items-center justify-center mb-5 group-hover:bg-gradient-brand group-hover:text-white transition"><Icon className="h-7 w-7" /></div>
              <h3 className="font-display font-bold text-lg text-primary">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function President() {
  return (
    <section className="py-24 sm:py-32 bg-secondary/40">
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 grid lg:grid-cols-5 gap-12 items-center">
        <div className="lg:col-span-2 reveal relative">
          <div className="absolute -inset-3 bg-gradient-brand opacity-20 blur-2xl rounded-3xl" />
          <img src={managerMsgImg} alt="Pont HKB, Abidjan de nuit" className="relative rounded-3xl shadow-elegant w-full object-cover aspect-[4/5]" loading="lazy" />
        </div>
        <div className="lg:col-span-3 reveal">
          <SectionTag>Le mot du Manager Général</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">Message du Manager Général</h2>
          <Quote className="h-10 w-10 text-royal/40 mt-8" />
          <blockquote className="mt-3 text-lg sm:text-xl text-foreground/85 leading-relaxed italic">
            « Notre mission est de mettre la technologie au service du développement africain.
            À travers FINDEO et nos différentes plateformes, nous voulons rapprocher les populations
            des services essentiels, créer des opportunités économiques et contribuer à l'émergence
            d'une Afrique plus connectée, plus inclusive et plus prospère. »
          </blockquote>
          <div className="mt-6">
            <div className="font-display font-bold text-foreground">Stéphane Koffi</div>
            <div className="text-sm text-muted-foreground">Manager Général — Groupe FINDEO</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Investors() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);
  return (
    <section id="investisseurs" className="py-24 sm:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-hero opacity-[0.06]" />
      <div className="relative container mx-auto max-w-6xl px-4 sm:px-6">
        <div className="rounded-3xl border border-border bg-card shadow-elegant p-10 sm:p-14 grid lg:grid-cols-5 gap-10 items-center reveal">
          <div className="lg:col-span-3">
            <SectionTag>Investisseurs</SectionTag>
            <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">Investir dans l'avenir de l'Afrique</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed text-lg">Le Groupe FINDEO recherche des partenaires financiers, institutionnels et stratégiques souhaitant participer à la transformation numérique du continent africain.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button type="button" onClick={() => setOpen(true)} data-testid="presentation-btn" className="inline-flex items-center gap-2 rounded-full bg-gradient-brand px-6 py-3.5 text-sm font-semibold text-white shadow-elegant hover:scale-[1.03] transition">
                <Download className="h-4 w-4" /> Consulter la présentation
              </button>
              <a href="#contact" className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3.5 text-sm font-semibold text-foreground hover:border-royal hover:text-royal transition">Contact investisseurs</a>
            </div>
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 gap-4">
            {[{ k: "5", v: "Plateformes" }, { k: "10+", v: "Pays ciblés" }, { k: "2021", v: "Création" }, { k: "USA", v: "Partenaire stratégique" }].map((s) => (
              <div key={s.v} className="rounded-2xl border border-border bg-secondary/40 p-5 text-center">
                <div className="text-2xl font-bold text-gradient-brand font-display">{s.k}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8" onClick={() => setOpen(false)} role="dialog" aria-modal="true" data-testid="presentation-modal">
          <div className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden bg-card shadow-elegant border border-border flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border bg-background">
              <div className="text-sm font-display font-semibold text-primary truncate">Présentation — Groupe FINDEO</div>
              <div className="flex items-center gap-2">
                <a href={PRESENTATION_IMG} download="Presentation_Groupe_FINDEO.png" className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-royal hover:text-royal transition"><Download className="h-3.5 w-3.5" /> Télécharger</a>
                <button type="button" onClick={() => setOpen(false)} aria-label="Fermer" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border hover:border-royal hover:text-royal transition"><X className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="overflow-auto bg-white">
              <img src={PRESENTATION_IMG} alt="Présentation Groupe FINDEO" className="w-full h-auto" />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Contact() {
  const [status, setStatus] = useState("idle");
  const fields = [
    { n: "prenom", l: "Prénom" },
    { n: "nom", l: "Nom" },
    { n: "societe", l: "Société", optional: true },
    { n: "pays", l: "Pays", optional: true },
    { n: "telephone", l: "Téléphone", type: "tel", optional: true },
    { n: "email", l: "Email", type: "email" },
  ];
  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {};
    fd.forEach((v, k) => { payload[k] = String(v); });
    // Sauvegarde en base (filet de sécurité, best-effort)
    axios.post(`${API}/contact`, payload).catch(() => {});
    // Envoi de l'email via FormSubmit
    try {
      const res = await fetch(`${CONTACT_EMAIL_ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: "Nouveau message - Groupe FINDEO",
          _template: "table",
          _captcha: "false",
          _autoresponse:
            "Bonjour,\n\nMerci d'avoir contacté le Groupe FINDEO. Nous avons bien reçu votre message et notre équipe vous répondra sous 48h.\n\nÀ très bientôt,\nL'équipe Groupe FINDEO\ninfo@groupefindeo.com — www.groupefindeo.com",
          ...payload,
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setStatus("sent");
      form.reset();
    } catch {
      setStatus("error");
    }
  };
  return (
    <section id="contact" className="py-24 sm:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-hero opacity-[0.04]" />
      <div className="relative container mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2 reveal">
          <SectionTag>Contact</SectionTag>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight text-primary">Parlons de votre projet</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">Une question, un partenariat, une opportunité ? Notre équipe vous répond sous 48h.</p>
          <ul className="mt-8 space-y-4">
            <li className="flex items-start gap-3"><MapPin className="h-5 w-5 text-royal mt-0.5" /><div><div className="font-semibold text-foreground">Siège</div><div className="text-sm text-muted-foreground">Abidjan – Côte d'Ivoire</div></div></li>
            <li className="flex items-start gap-3"><Mail className="h-5 w-5 text-royal mt-0.5" /><div><div className="font-semibold text-foreground">Email</div><a href="mailto:info@groupefindeo.com" className="text-sm text-muted-foreground hover:text-royal">info@groupefindeo.com</a></div></li>
            <li className="flex items-start gap-3"><Phone className="h-5 w-5 text-royal mt-0.5" /><div><div className="font-semibold text-foreground">Téléphone</div><div className="text-sm text-muted-foreground">+225 07 88 00 87 81<br/>+225 07 19 57 47 47<br/>+1 (518) 535-5959</div></div></li>
          </ul>
          <div className="mt-8 flex gap-3">
            {[Facebook, Linkedin, Instagram, MessageCircle].map((I, i) => (
              <a key={i} href="#" aria-label="social" className="h-11 w-11 rounded-full border border-border flex items-center justify-center text-foreground/70 hover:bg-gradient-brand hover:text-white hover:border-transparent transition"><I className="h-5 w-5" /></a>
            ))}
          </div>
        </div>
        <form onSubmit={onSubmit} data-testid="contact-form" className="lg:col-span-3 reveal rounded-3xl bg-card border border-border shadow-elegant p-6 sm:p-10 grid sm:grid-cols-2 gap-5">
          {fields.map((f) => (
            <div key={f.n} className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-foreground/70">{f.l}</label>
              <input required={!f.optional} type={f.type ?? "text"} name={f.n} maxLength={120} data-testid={`contact-${f.n}`} className="rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-royal/40 focus:border-royal transition" />
            </div>
          ))}
          <div className="sm:col-span-2 flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-foreground/70">Message</label>
            <textarea required name="message" rows={5} maxLength={2000} data-testid="contact-message" className="rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-royal/40 focus:border-royal transition" />
          </div>
          <div className="sm:col-span-2 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-muted-foreground" data-testid="contact-status">
              {status === "sent" ? "✓ Message envoyé, merci !" : status === "error" ? "Une erreur est survenue, réessayez." : "Vos données restent confidentielles."}
            </p>
            <button type="submit" disabled={status === "sending"} data-testid="contact-submit" className="inline-flex items-center gap-2 rounded-full bg-gradient-brand px-7 py-3.5 text-sm font-semibold text-white shadow-elegant hover:scale-[1.03] transition disabled:opacity-60">
              {status === "sending" ? "Envoi…" : status === "sent" ? "Message envoyé" : "Envoyer le message"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
